import { $mobx, _getGlobalState, isObservableObject, observable } from 'mobx';
import type { ITileManagerModel, IConnectionModel } from '@/interfaces/models';
import { isNativeConnectionSelectorSource } from '@/models/connections/ConnectionModel';
import {
  isConstructedTileCollection,
  hasNativeTileLookups,
} from '@/models/TileManagerModel';
import { TileInstance, isConstructedTileInstance } from '@/models/tiles/TileInstance';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import { TileType } from '@/models/TileTypes';
import { Point } from '@/models/Point';
import { isOwnedScalarTile } from '@/utils/OwnedBundleVisualData';

const fieldNames = [
  'tiles',
  'tileType',
  'state',
  'globalTilePosition',
  'meta',
  'rotation',
  'partOfChip',
  'cable',
  'effectiveIoSides',
  'logicNodeIds',
  'laneNodeIds',
  'connections',
  'adapterPin',
  'adapterPort',
  'cablePort',
];
const probe = observable.object(Object.fromEntries(fieldNames.map((key) => [key, null])));
const probeAdmin = Object.getOwnPropertyDescriptor(probe, $mobx)?.value;
const probeValue =
  typeof probeAdmin?.values_?.get === 'function'
    ? probeAdmin.values_.get('state')
    : undefined;
// The Node production bundle can use MobX's minified CJS administration. Its
// unknown private shape keeps conservative graph traversal instead of crashing.
const valuePrototype = probeValue && Object.getPrototypeOf(probeValue);
const nativeGet = valuePrototype?.get;
const nativeReportObserved = valuePrototype?.reportObserved;
const descriptors = new Map(
  fieldNames.map((key) => [key, Object.getOwnPropertyDescriptor(probe, key)!]),
);

function ordinaryField(value: object, key: string): boolean {
  const descriptor = Object.getOwnPropertyDescriptor(value, key);
  if (!descriptor) return !(key in value);
  const admin = Object.getOwnPropertyDescriptor(value, $mobx)?.value;
  const field = admin?.values_?.get(key);
  let expected = descriptors.get(key);
  if (!expected) {
    expected = Object.getOwnPropertyDescriptor(observable.object({ [key]: null }), key)!;
    descriptors.set(key, expected);
  }
  return (
    !!field &&
    Object.getPrototypeOf(field) === valuePrototype &&
    field.get === nativeGet &&
    field.reportObserved === nativeReportObserved &&
    field.dehancer === undefined &&
    field.onBOL === undefined &&
    field.onBUOL === undefined &&
    !field.interceptors_?.length &&
    !field.changeListeners_?.length &&
    !admin.interceptors_?.length &&
    !admin.changeListeners_?.length &&
    descriptor.get === expected.get &&
    descriptor.set === expected.set
  );
}

/** Built-in state can contain observable data, but not custom getters that may
 * read decoration elsewhere in the circuit. Unknown shapes keep the old graph. */
function ordinaryState(value: unknown, seen: Set<object>): boolean {
  if (value === null || typeof value !== 'object') return typeof value !== 'function';
  if (seen.has(value)) return true;
  if (
    Object.getPrototypeOf(value) !== Object.prototype ||
    !isObservableObject(value) ||
    'toJSON' in value
  )
    return false;
  const admin = Object.getOwnPropertyDescriptor(value, $mobx)?.value;
  if (
    !admin ||
    (admin.proxy_ ?? admin.target_) !== value ||
    admin.keysAtom_?.onBOL !== undefined ||
    admin.keysAtom_?.onBUOL !== undefined
  )
    return false;
  seen.add(value);
  return Object.getOwnPropertyNames(value).every(
    (key) =>
      ordinaryField(value, key) &&
      ordinaryState((value as Record<string, unknown>)[key], seen),
  );
}

/** Only the owned, scalar built-in editor path can omit decorative adjacency.
 * Custom managers, builders, state readers and geometry retain the old behavior. */
export function canSeparateDecoration(
  manager: ITileManagerModel,
  subset?: TileInstance[],
  connections?: IConnectionModel,
): boolean {
  if (typeof nativeGet !== 'function' || typeof nativeReportObserved !== 'function')
    return false;
  const spyListeners = _getGlobalState().spyListeners;
  if (
    !Array.isArray(spyListeners) ||
    spyListeners.length ||
    !hasNativeTileLookups(manager)
  )
    return false;
  if (connections && !isNativeConnectionSelectorSource(connections)) return false;
  if (!ordinaryField(manager, 'tiles')) return false;
  const collection = manager.tiles;
  if (!isConstructedTileCollection(manager, collection)) return false;
  const tiles = subset ?? collection;
  const types = new Set<TileType>();
  for (const tile of tiles) {
    if (
      !isConstructedTileInstance(tile) ||
      Object.getPrototypeOf(tile) !== TileInstance.prototype ||
      ![
        'tileType',
        'state',
        'globalTilePosition',
        'meta',
        'effectiveIoSides',
        'logicNodeIds',
        'laneNodeIds',
        'adapterPin',
        'adapterPort',
        'cablePort',
      ].every((key) => ordinaryField(tile, key))
    )
      return false;
    types.add(tile.tileType);
  }
  if (
    !types.has(TileType.Structure) ||
    !tileDefinitionRegistry.hasNativeGraphBuilders?.(types)
  )
    return false;
  const seen = new Set<object>();
  for (const tile of tiles) {
    if (
      !isOwnedScalarTile(tile) ||
      !['rotation', 'partOfChip', 'cable', 'connections'].every((key) =>
        ordinaryField(tile.meta, key),
      ) ||
      tile.meta.cable ||
      !ordinaryState(tile.state, seen) ||
      !ordinaryState(tile.logicNodeIds, seen) ||
      !ordinaryState(tile.laneNodeIds, seen) ||
      !ordinaryState(tile.meta.connections, seen) ||
      (tile.effectiveIoSides !== undefined && !ordinaryState(tile.effectiveIoSides, seen))
    )
      return false;
    const global = tile.globalTilePosition;
    const locationDescriptor = Object.getOwnPropertyDescriptor(global, 'location');
    const locationAtom = Object.getOwnPropertyDescriptor(tile, $mobx)?.value.values_.get(
      'location',
    );
    if (
      Object.getPrototypeOf(global) !== Object.prototype ||
      !locationDescriptor ||
      !('value' in locationDescriptor) ||
      locationAtom?.onBOL !== undefined ||
      locationAtom?.onBUOL !== undefined ||
      locationAtom?.derivation !==
        Object.getOwnPropertyDescriptor(TileInstance.prototype, 'location')?.get
    )
      return false;
    const location = tile.location;
    const prototype = Object.getPrototypeOf(location);
    if (
      (prototype !== Object.prototype && prototype !== Point.prototype) ||
      'toJSON' in location ||
      Object.keys(location).length !== 2
    )
      return false;
    for (const key of ['x', 'y']) {
      const descriptor = Object.getOwnPropertyDescriptor(location, key);
      if (!descriptor || !('value' in descriptor) || typeof descriptor.value !== 'number')
        return false;
    }
  }
  return true;
}
